import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from './query.js';
import {
  api,
  type Template,
  type SeedBody,
  type MarkPoint,
  type SeedDistractor,
  type AiSuggestion,
  type AiSuggestResponse,
  type SelfGradeResult,
} from './api.js';
import {
  LifecycleChip,
  ReviewVerdictChip,
  OriginChip,
  StringListEditor,
  IndicativeList,
  McqOptions,
  PointRow,
  LevelRow,
  SuggestionDiff,
} from './widgets.js';

/**
 * Field-based question reviewer (spec §5 + §6). Replaces raw-JSON editing entirely:
 *
 *  - Reading-first: by default shows a rendered, exam-paper-like view (question + mark
 *    scheme table). An Edit toggle reveals the TYPED form — no JSON textarea, ever.
 *  - SME edits only: stem, indicative_content, model_answer, MCQ options, examiner_traps.
 *    Canonical fields (level bands, marks, tier, portal_key, source, ao weights,
 *    rubric_injected_levels) are shown read-only and preserved untouched on Save.
 *  - MCQ normalization: the stem is rendered/edited as the PROMPT ONLY; embedded option
 *    blocks ("A) … Mark Scheme: C") are stripped on display. Options come from distractors[].
 *  - Two on-demand AI buttons: Suggest edits (inline accept/reject diff) and Rubric
 *    self-consistency grade.
 *  - Save reconstructs a valid seed_body from the typed fields, preserving every read-only
 *    key, and reuses PATCH /api/template/:id + POST /api/review.
 *
 * M9 — chip rework: the toolbar's three status chips are now LifecycleChip,
 *      ReviewVerdictChip and OriginChip with explicit "Axis: value" labels so
 *      SMEs can tell at a glance what each one means.
 */

const HEADER_LABELS: Record<string, string> = {
  path6: 'Path',
  conditional: 'Conditional',
  final: 'Final',
};

/** Strip an embedded MCQ options/answer block so the stem is just the prompt (spec §5). */
function stripMcqOptionsFromStem(stem: string): string {
  // Cut everything from the first "A)"/"A." option line or an explicit "Mark Scheme:".
  const lines = stem.split('\n');
  const out: string[] = [];
  for (const line of lines) {
    const t = line.trim();
    if (/^[A-D][).:]\s+/.test(t)) break;
    if (/^mark\s*scheme\s*:/i.test(t)) break;
    out.push(line);
  }
  return out.join('\n').trim();
}

function isMcq(seed: SeedBody): boolean {
  return seed.command_word === 'MCQ' || (Array.isArray(seed.distractors) && seed.distractors.length > 0);
}

export function QuestionEditor({ tmpl, invalidateKeys }: { tmpl: Template; invalidateKeys: unknown[][] }) {
  const qc = useQueryClient();
  const seed = tmpl.seed_body;

  const [editing, setEditing] = useState(false);

  // Editable typed state (seeded from seed_body / row).
  const [stem, setStem] = useState('');
  const [indicative, setIndicative] = useState<string[]>([]);
  const [points, setPoints] = useState<MarkPoint[]>([]);
  const [modelAnswer, setModelAnswer] = useState('');
  const [distractors, setDistractors] = useState<SeedDistractor[]>([]);
  const [traps, setTraps] = useState<string[]>([]);
  const [notes, setNotes] = useState('');

  // AI helper state.
  const [suggestions, setSuggestions] = useState<AiSuggestion[]>([]);
  const [selfGrade, setSelfGrade] = useState<SelfGradeResult | null>(null);

  useEffect(() => {
    const s = tmpl.seed_body;
    const mcq = s ? isMcq(s) : false;
    setStem(s ? (mcq ? stripMcqOptionsFromStem(s.stem) : s.stem) : '');
    setIndicative(s?.mark_scheme?.indicative_content ?? []);
    setPoints(s?.mark_scheme?.points ?? []);
    setModelAnswer(s?.model_answer ?? '');
    setDistractors(s?.distractors ?? []);
    setTraps(tmpl.examiner_traps ?? []);
    setNotes(tmpl.sme_notes ?? '');
    setSuggestions([]);
    setSelfGrade(null);
    setEditing(false);
  }, [tmpl.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const invalidate = () => {
    for (const key of invalidateKeys) qc.invalidateQueries({ queryKey: key });
  };

  /** Reconstruct a valid seed_body from typed fields, preserving all read-only keys. */
  function rebuildSeedBody(): SeedBody | null {
    if (!seed) return null;
    const next: SeedBody = {
      ...seed,
      stem,
      mark_scheme: { ...seed.mark_scheme },
    };
    if (seed.mark_scheme.type === 'points') {
      next.mark_scheme.points = points;
    }
    // Level bands are canonical — copied through untouched (never edited here).
    if (indicative.length > 0) next.mark_scheme.indicative_content = indicative.filter((x) => x.trim());
    else delete next.mark_scheme.indicative_content;
    if (modelAnswer.trim()) next.model_answer = modelAnswer;
    else delete next.model_answer;
    if (isMcq(seed)) next.distractors = distractors;
    return next;
  }

  // patch4: include sme_notes so "Save" actually persists notes inline
  // (previously the only path that saved sme_notes was Approve/Reject —
  // identical bug to ConceptEditor).
  const save = useMutation({
    mutationFn: () => {
      const seedBody = rebuildSeedBody();
      return api.patch(`/api/template/${tmpl.id}`, {
        seed_body: seedBody,
        examiner_traps: traps.filter((t) => t.trim().length > 0),
        sme_notes: notes,
      });
    },
    onSuccess: invalidate,
  });

  const review = useMutation({
    mutationFn: (review_status: 'approved' | 'rejected') =>
      api.post('/api/review', { table: 'battle', id: tmpl.id, review_status, sme_notes: notes }),
    onSuccess: invalidate,
  });

  const suggest = useMutation({
    mutationFn: () => api.post<AiSuggestResponse>('/api/ai/suggest', { template_id: tmpl.id }),
    onSuccess: (res) => {
      setSuggestions(res.suggestions);
      setEditing(true); // suggestions act on the typed form
    },
  });

  const selfGradeMut = useMutation({
    mutationFn: () => api.post<SelfGradeResult>('/api/ai/self-grade', { template_id: tmpl.id }),
    onSuccess: (res) => setSelfGrade(res),
  });

  function applySuggestion(s: AiSuggestion) {
    switch (s.field) {
      case 'stem':
        if (typeof s.suggested === 'string') setStem(s.suggested);
        break;
      case 'indicative_content':
        if (Array.isArray(s.suggested)) setIndicative(s.suggested.map((x) => String(x)));
        break;
      case 'model_answer':
        if (typeof s.suggested === 'string') setModelAnswer(s.suggested);
        break;
      case 'examiner_traps':
        if (Array.isArray(s.suggested)) setTraps(s.suggested.map((x) => String(x)));
        break;
      case 'distractors':
        if (Array.isArray(s.suggested)) setDistractors(s.suggested as SeedDistractor[]);
        break;
    }
    dismiss(s);
  }
  function dismiss(s: AiSuggestion) {
    setSuggestions((cur) => cur.filter((x) => x !== s));
  }

  if (!seed) {
    return (
      <div className="card err" data-testid={`question-${tmpl.id}`}>
        This question has no seed_body and cannot be reviewed.
      </div>
    );
  }

  const mcq = isMcq(seed);
  const ms = seed.mark_scheme;

  return (
    <div className="card question-editor" data-testid={`question-${tmpl.id}`}>
      {/* ---- read-only context header (spec §5) ---- */}
      {/* M9 — labelled chips. Old: `<QualityChip /><Chip kind=status label=status /><ReviewChip />`
              all three rendered as same-shape pills with no axis. Now each carries an axis label. */}
      <div className="toolbar question-header">
        <span style={{ fontFamily: 'var(--mono)' }}>
          {seed.portal_key
            ? `Portal ${seed.portal_key.replace('-', ' ↔ ')}`
            : `Cluster ${tmpl.cluster_id}`}
          {seed.tier ? ` · ${HEADER_LABELS[seed.tier] ?? seed.tier}` : ''}
          {` · ${seed.marks}m · ${seed.command_word}`}
        </span>
        {seed.source?.section && <span className="chip source" title={seed.source.doc}>{seed.source.section}</span>}
        <OriginChip quality={tmpl.seed_body?.source_quality ?? 'GENUINE'} />
        <LifecycleChip status={tmpl.status} />
        <ReviewVerdictChip status={tmpl.review_status} />
        {tmpl.edited_by_sme && <span className="chip edited">edited</span>}
        <span style={{ flex: 1 }} />
        <button className="sm" type="button" onClick={() => setEditing((e) => !e)} data-testid={`button-edit-toggle-${tmpl.id}`}>
          {editing ? 'Done editing' : 'Edit'}
        </button>
      </div>

      {/* ---- AI helper buttons (spec §6) ---- */}
      <div className="row-actions ai-bar" style={{ marginBottom: 10 }}>
        <button
          className="sm"
          type="button"
          onClick={() => suggest.mutate()}
          disabled={suggest.isPending}
          data-testid={`button-ai-suggest-${tmpl.id}`}
        >
          {suggest.isPending ? 'Thinking…' : 'Suggest edits'}
        </button>
        <button
          className="sm"
          type="button"
          onClick={() => selfGradeMut.mutate()}
          disabled={selfGradeMut.isPending}
          data-testid={`button-ai-selfgrade-${tmpl.id}`}
        >
          {selfGradeMut.isPending ? 'Grading…' : 'Rubric self-consistency grade'}
        </button>
        {suggest.isError && <span className="err">Suggest failed</span>}
        {selfGradeMut.isError && <span className="err">Self-grade failed</span>}
      </div>

      {selfGrade && (
        <div
          className={`self-grade ${selfGrade.verdict === 'short' ? 'err' : 'ok'}`}
          data-testid={`self-grade-${tmpl.id}`}
        >
          This rubric awards <strong>{selfGrade.score}/{selfGrade.max}</strong> to its own model answer
          {selfGrade.verdict === 'short' ? ' — below full marks. The scheme may not reward its own ideal answer.' : '.'}
          {selfGrade.feedback && <div className="muted">{selfGrade.feedback}</div>}
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="suggestions" data-testid={`suggestions-${tmpl.id}`}>
          {suggestions.map((s, i) => (
            <SuggestionDiff
              key={`${s.field}-${i}`}
              suggestion={s}
              onAccept={() => applySuggestion(s)}
              onReject={() => dismiss(s)}
              testid={`suggestion-${tmpl.id}-${i}`}
            />
          ))}
        </div>
      )}

      {/* ---- body: read view OR typed edit form ---- */}
      {!editing ? (
        <ReadView seed={seed} stem={stem} traps={traps} mcq={mcq} />
      ) : (
        <div className="edit-form">
          <label>Question shown to students</label>
          <textarea value={stem} onChange={(e) => setStem(e.target.value)} data-testid={`input-stem-${tmpl.id}`} />

          {mcq ? (
            <>
              <label>Options (radio = correct)</label>
              <McqOptions options={distractors} onChange={setDistractors} testidPrefix={`mcq-${tmpl.id}`} />
            </>
          ) : (
            <>
              <label>Mark scheme</label>
              {ms.type === 'points' ? (
                <div className="row-list">
                  {points.map((p, i) => (
                    <PointRow
                      key={i}
                      point={p}
                      onChange={(next) => setPoints(points.map((x, j) => (j === i ? next : x)))}
                      onRemove={() => setPoints(points.filter((_, j) => j !== i))}
                      testid={`point-${tmpl.id}-${i}`}
                    />
                  ))}
                  <button
                    className="ghost sm"
                    type="button"
                    style={{ alignSelf: 'flex-start' }}
                    onClick={() => setPoints([...points, { marks: 1, criterion: '' }])}
                    data-testid={`button-add-point-${tmpl.id}`}
                  >
                    + Add point
                  </button>
                </div>
              ) : (
                <LevelTable seed={seed} />
              )}

              <label>Indicative content</label>
              <IndicativeList values={indicative} onChange={setIndicative} testidPrefix={`indicative-${tmpl.id}`} />

              <label>Model answer</label>
              <textarea
                value={modelAnswer}
                onChange={(e) => setModelAnswer(e.target.value)}
                data-testid={`input-model-answer-${tmpl.id}`}
              />
            </>
          )}

          <label>Examiner traps</label>
          <StringListEditor values={traps} onChange={setTraps} placeholder="trap text" testidPrefix={`trap-${tmpl.id}`} />
        </div>
      )}

      {/* ---- review verdict ---- */}
      <label style={{ marginTop: 12 }}>SME notes</label>
      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`input-tmpl-notes-${tmpl.id}`} />
      <div className="row-actions" style={{ marginTop: 10 }}>
        <button
          className="primary sm"
          type="button"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          data-testid={`button-save-tmpl-${tmpl.id}`}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button
          className="sm"
          type="button"
          onClick={() => review.mutate('approved')}
          disabled={review.isPending}
          data-testid={`button-approve-tmpl-${tmpl.id}`}
        >
          Approve
        </button>
        <button
          className="danger sm"
          type="button"
          onClick={() => review.mutate('rejected')}
          disabled={review.isPending}
          data-testid={`button-reject-tmpl-${tmpl.id}`}
        >
          Reject
        </button>
        {save.isError && <span className="err">Save failed</span>}
        {review.isError && <span className="err">Review failed</span>}
        {(save.isSuccess || review.isSuccess) && <span className="ok">Saved</span>}
      </div>
    </div>
  );
}

// ---- read-only sub-views --------------------------------------------------

/** Read-only level-band table (canonical bands shown for context). */
function LevelTable({ seed }: { seed: SeedBody }) {
  const levels = seed.mark_scheme.levels ?? [];
  if (levels.length === 0) return <p className="muted">No level bands.</p>;
  return (
    <table className="mark-scheme-table" data-testid="level-table">
      <thead>
        <tr>
          <th>Level</th>
          <th>Marks</th>
          <th>Descriptor (canonical · read-only)</th>
        </tr>
      </thead>
      <tbody>
        {levels.map((l) => (
          <LevelRow key={l.level} level={l} />
        ))}
      </tbody>
    </table>
  );
}

/** Rendered exam-paper-like view (default, reading-first). */
function ReadView({ seed, stem, traps, mcq }: { seed: SeedBody; stem: string; traps: string[]; mcq: boolean }) {
  const ms = seed.mark_scheme;
  return (
    <div className="read-view" data-testid="read-view">
      <p className="question-stem" style={{ whiteSpace: 'pre-wrap' }}>{stem}</p>

      {mcq ? (
        <ol className="mcq-options" type="A">
          {(seed.distractors ?? []).map((d) => (
            <li key={d.option} className={d.is_correct ? 'correct' : ''}>
              {d.text}
              {d.is_correct && <span className="chip genuine" style={{ marginLeft: 6 }}>correct</span>}
            </li>
          ))}
        </ol>
      ) : (
        <>
          {ms.type === 'points' && (
            <table className="mark-scheme-table">
              <thead>
                <tr>
                  <th>Marks</th>
                  <th>Criterion</th>
                </tr>
              </thead>
              <tbody>
                {(ms.points ?? []).map((p, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'var(--mono)' }}>{p.marks}</td>
                    <td>{p.criterion}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {ms.type === 'levels' && <LevelTable seed={seed} />}
          {(ms.indicative_content?.length ?? 0) > 0 && (
            <>
              <h3 style={{ fontSize: 13, margin: '10px 0 4px' }}>Indicative content</h3>
              <ul>
                {ms.indicative_content!.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </>
          )}
          {seed.model_answer && (
            <details className="model-answer">
              <summary>Model answer</summary>
              <p style={{ whiteSpace: 'pre-wrap' }}>{seed.model_answer}</p>
            </details>
          )}
        </>
      )}

      {traps.length > 0 && (
        <>
          <h3 style={{ fontSize: 13, margin: '10px 0 4px' }}>Examiner traps</h3>
          <ul>
            {traps.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
